<template>
  <div>
    <b-row>
      <b-col :class="`col-${resourceCols} pr-0`">
        <small v-if="showLabel" class="d-block">{{ trans('resource') }}</small>
        <treeselect
          ref="location"
          :options="locationOptions"
          :placeholder="trans('resource')"
          v-model="location_id"
          :class="!!variant ? `variant-${variant}` : ''"
          :title="label"
          :disabled="busy || disabled"
          :defaultExpandLevel="10000"
        >
          <div slot="before-list">
            <small v-if="hasEquipmentWithSkus" class="d-block text-muted text-center">
              <i class="fa fa-fw fa-barcode"></i>
              {{ trans('scan_equipment_barcode') }}
            </small>
          </div>
          <div slot="value-label" slot-scope="{ node }">
            <small>{{ node.label }}</small>
          </div>
          <label slot="option-label" slot-scope="{ node }">
            <span>{{ node.label }}</span>
            <b-badge v-if="node.raw.conflict" variant="danger">{{ trans('conflict') }}</b-badge>
          </label>
        </treeselect>
      </b-col>
      <b-col v-if="organization.has_disable_locations" class="col-1 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('status') }}</small>
        <location-state
          v-if="!!location"
          :location="location"
          button
          button-size="sm"
          button-variant-outline
          @click="onClickLocationState"
        ></location-state>
      </b-col>
      <b-col :class="`col-${durationCols} pr-0`">
        <small v-if="showLabel" class="d-block">{{ trans(mode == 'duration' ? 'date' : 'start') }}</small>
        <datetime
          v-model="start_date"
          class="form-control-sm"
          :config="dateOptions"
          :placeholder="trans('start_date')"
          :disabled="busy || disabled"
          @dp-hide="onChangeStart"
        />
      </b-col>
      <b-col class="col-1 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('time') }}</small>
        <datetime
          v-model="start_time"
          class="form-control-sm"
          :config="timeOptions"
          :placeholder="trans('start_time')"
          :disabled="busy || disabled"
          @dp-hide="onChangeStart"
        />
      </b-col>
      <b-col v-if="mode == 'duration'" class="col-2 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('duration') }}</small>
        <b-form-select
          v-model="duration"
          size="sm"
          :options="durationOptions"
          :placeholder="trans('duration')"
          :disabled="busy || disabled"
          @change="onChangeDuration"
        />
      </b-col>
      <b-col v-if="mode == 'end'" class="col-2 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('end') }}</small>
        <datetime
          v-model="end_date"
          class="form-control-sm"
          :config="dateOptions"
          :placeholder="trans('end_date')"
          :disabled="busy || disabled"
          @dp-hide="onChangeEnd"
        />
      </b-col>
      <b-col v-if="mode == 'end'" class="col-1 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('time') }}</small>
        <datetime
          v-model="end_time"
          class="form-control-sm"
          :config="timeOptions"
          :placeholder="trans('end_time')"
          :disabled="busy || disabled"
          @dp-hide="onChangeEnd"
        />
      </b-col>
      <b-col v-if="showPrice" class="col-1 pr-0">
        <small v-if="showLabel" class="d-block">{{ trans('price') }}</small>
        <b-form-input
          v-model="price"
          size="sm"
          type="number"
          step="0.01"
          min="0"
          :state="priceState"
          @change="onChange"
        />
      </b-col>
      <b-col class="col-1 pl-0 text-center">
        <small v-if="showLabel" class="d-block">&nbsp;</small>
        <b-button
          variant="link"
          size="sm"
          class="text-danger px-0"
          @click="$emit('delete', index)"
          :disabled="busy || disabled"
        >
          <i class="fa fa-fw fa-times"></i>
        </b-button>
      </b-col>
    </b-row>
    <div>
      <small v-if="busy">
        <i class="fa fa-fw fa-circle-notch fa-spin"></i>
        {{ trans('please_wait') }}
      </small>
      <small v-if="error" class="text-danger">
        {{ error }}
      </small>
      <b-button v-if="!busy && !error && conflict" variant="link" size="sm" class="p-0 text-danger" @click="onClickConflict">
        {{ trans('not_available') }}
      </b-button>
    </div>
    <location-state-modal
      :id="`location-state-modal-${index}`"
      :organization="organization"
      :location="location"
      @location="$emit('location', $event)"
    ></location-state-modal>
    <location-conflicts-modal
      :id="`location-conflicts-modal-${index}`"
      :organization="organization"
      :location="location"
      :start="start"
      :end="end"
      :events="conflictingEvents"
      :not-available-events="conflictingNotAvailableEvents"
    ></location-conflicts-modal>
  </div>
</template>

<script>
  import Treeselect from '@riophae/vue-treeselect'
  import Datetime from 'vue-bootstrap-datetimepicker'
  import LocationState from './../location/LocationState'
  import LocationStateModal from './../location/LocationStateModal'
  import LocationConflictsModal from './LocationConflictsModal'

  let dateFormat = 'ddd LL'
  let timeFormat = 'LT'

  function deserialize(value) {
    let location_id = value.location_id
    let start_date = value.start_date
    let start_time = value.start_time
    let duration = value.duration
    let end_date = start_date
    let end_time = start_time
    let price = value.price

    if (!!start_date && !!start_time) {
      let start = moment(start_date, dateFormat)
      let time = moment(start_time, timeFormat)
      start.set({
        hour: time.get('hour'),
        minute: time.get('minute'),
      })
      let end = start.add(duration, 'minutes')
      end_date = end.format(dateFormat)
      end_time = end.format(timeFormat)
    }

    return [location_id, start_date, start_time, duration, end_date, end_time, price]
  }
  
  export default {
    props: {
      organization: Object,
      booking: Object,
      locations: Array,
      index: Number,
      isFirst: Boolean,
      value: Object, // event
      disabled: {
        type: Boolean,
        default: false,
      },
      mode: {
        type: String,
        default: 'duration', // duration or end
      },
      showPrice: {
        type: Boolean,
        default: false,
      },
      initialPrice: {
        type: Number,
        default: null,
      },
      per60MinuteDuration: {
        type: Number,
        default: null,
      },
      fetchEvents: Function,
      fetchStaffAvailabilityEvents: {
        type: Function,
        default: null
      },
      variant: { // success, danger, info or null
        type: String,
        default: null,
      },
    },
    components: {
      Treeselect,
      Datetime,
      LocationState,
      LocationStateModal,
      LocationConflictsModal,
    },
    data() {
      let [location_id, start_date, start_time, duration, end_date, end_time, price] = deserialize(this.value)
      return {
        trans: trans,
        moment: moment,
        busy: false,
        error: null,
        location_id: location_id,
        start_date: start_date,
        start_time: start_time,
        duration: duration,
        end_date: end_date,
        end_time: end_time,
        price: price,
        locationOptions: [],
        dateOptions: {
          locale: window.lang,
          format: dateFormat,
        },
        timeOptions: {
          locale: window.lang,
          format: timeFormat,
          stepping: 5,
        },
        events: [],
        lastLoadUrl: null,
        last_keydown_timestamp: null,
        staffAvailabilityEvents: null,
      }
    },
    computed: {
      resourceCols() {
        let cols = this.organization.has_disable_locations ? 4 : 5
        if (this.showPrice) cols -= 1
        return cols
      },
      durationCols() {
        let cols = this.mode == 'duration' ? 3 : 2
        return cols
      },
      location() {
        if (!this.location_id) return null
        return _.find(this.locations || [], {id: this.location_id}) || null
      },
      start() {
        if (!this.start_date || !this.start_time) return null
        let start = moment(this.start_date, dateFormat)
        let time = moment(this.start_time, timeFormat)
        start.set({
          hour: time.get('hour'),
          minute: time.get('minute'),
        })
        return start
      },
      end() {
        if (!this.start || !this.duration) return null
        return moment(this.start).add(this.duration, 'minute')
      },
      label() {
        if (!this.location) return null
        let label = this.location.name
        if (this.location.type == 'equipment' && !!this.location.sku) label += ` (${this.location.sku})`
        return label
      },
      showLabel() {
        return this.isFirst === true || this.index == 0 && this.isFirst !== false
      },
      hasEquipmentWithSkus() {
        return _.find(this.locations || [], location => {
          if (location.type == 'equipment' && !!location.sku) return true
          return false
        })
      },
      durationOptions() {
        let options = []
        for (let i = 5; i <= 1080; i += 5) {
          let h = Math.floor(i/60)
          let m = i % 60
          options.push({
            value: i,
            text: (h > 0 ? `${h}h` : '') + (h > 0 && m > 0 ?' ' : '') + (m > 0 ? `${m}m` : '')
          })
        }
        return options
      },
      conflictingLocationIds() {
        return _.reduce(this.events || [], (used, event) => {
          if (!!event.end && moment(event.end).isBefore(this.start) ||
            moment(event.start).isAfter(this.end))
            return used
          if (!this.booking || this.booking.id != event.extendedProps.booking_id) {
            if (this.location_id == event.resourceId &&
              used.indexOf(event.resourceId) === -1)
              used.push(event.resourceId)
          }
          _.each(event.extendedProps.overlapping_location_ids || [], id => {
            if (this.location_id == id &&
              used.indexOf(id) === -1)
              used.push(id)
          })
          return used
        }, [])
      },
      conflictingEvents() {
        if (this.conflictingLocationIds.length == 0) return []
        return _.reduce(this.events || [], (events, e) => {
          if (this.conflictingLocationIds.indexOf(e.resourceId) >= 0) {
            events.push(e)
          }
          return events
        }, [])
      },
      conflictingNotAvailableEvents() {
        if (this.unavailableLocationIds.length == 0) return []
        return _.reduce(this.events || [], (events, e) => {
          if (this.unavailableLocationIds.indexOf(e.resourceId) >= 0) {
            events.push(e)
          }
          return events
        }, [])
      },
      priceState() {
        if (isNaN(this.price) || parseFloat(this.price) < 0) return false
        return null
      },
      conflict() {
        if (!this.location_id) return false
        return this.conflictingLocationIds.indexOf(this.location_id) >= 0 || this.unavailableLocationIds.indexOf(this.location_id) >= 0
      },
      serialized() {
        return _.assign({}, this.value || {}, {
          location_id: this.location_id,
          start_date: this.start_date,
          start_time: this.start_time,
          duration: this.duration,
          price: this.price,
          conflict: this.conflict,
        })
      },
      canSave() {
        return !!this.location_id &&
          !!this.start_date &&
          !!this.start_time &&
          !!this.duration &&
          (!this.showPrice || this.priceState !== false)
      },
      unavailableLocationIds() {        
        if (!this.organization.has_staff_schedule || this.staffAvailabilityEvents === null) {
          return [];
        }

        const staffLocations = this.locations.filter(location => location.type === "staff");
        if (this.staffAvailabilityEvents.length === 0) {
          return staffLocations.map(location => location.id);
        }

        return staffLocations
          .filter(location => {
            const eventsForLocation = this.staffAvailabilityEvents.filter(
              event => event.location_id === location.id
            );
            const hasEventInTimeRange = eventsForLocation.some(event => {
              const eventStart = moment(event.start);
              const eventEnd = moment(event.end);
              const startTime = moment(this.start);
              const endTime = moment(this.end);

              return startTime.isBetween(eventStart, eventEnd, null, "[]") &&
                    endTime.isBetween(eventStart, eventEnd, null, "[]");
            });

            return !hasEventInTimeRange;
          })
          .map(location => location.id);
      }
    },
    watch: {
      value() {
        [this.location_id, this.start_date, this.start_time, this.duration, this.end_date, this.end_time, this.price] = deserialize(this.value)
      },
      location_id() {
        this.onChange()
      },
      locations() {
        this.hydrateLocationOptions()
      },
      conflict() {
        this.onChange()
      },
    },
    methods: {
      onClickConflict() {
        this.$bvModal.show(`location-conflicts-modal-${this.index}`)
      },
      onClickLocationState() {
        this.$bvModal.show(`location-state-modal-${this.index}`)
      },
      onLocationKeydown(e) {
        // Capture and abort the Enter key sent by the scanner
        // This gives the Treeselect time to do its search and present possible matched values
        let timestamp = Date.now()
        if (e.keyCode == 13 &&
          !!this.last_keydown_timestamp &&
          (timestamp - this.last_keydown_timestamp < 100)) {
          e.stopImmediatePropagation()
          return false
        }
        this.last_keydown_timestamp = timestamp
      },
      onChangeDuration() {
        this.maybeRecalculatePrice()
        this.onChange()
      },
      onChangeStart() {
        this.maybeRecalculatePrice()
        this.onChange()
      },
      onChangeEnd() {
        if (!this.start || !this.end_date || !this.end_time) return
        let start = moment(this.start)
        let end = moment(this.end_date, dateFormat)
        let time = moment(this.end_time, timeFormat)
        end.set({
          hour: time.get('hour'),
          minute: time.get('minute'),
        })
        this.duration = end.diff(start, 'minutes')
        this.maybeRecalculatePrice()
        this.onChange()
      },
      onChange() {
        // console.log('onChange', this.serialized, this.canSave)
        if (this.canSave && !_.isEqual(this.value, this.serialized)) {
          let reloadEvents = !this.value ||
            this.value.location_id != this.serialized.location_id ||
            this.value.start_date != this.serialized.start_date ||
            this.value.start_time != this.serialized.start_time ||
            this.value.duration != this.serialized.duration
          this.$emit('input', this.serialized)
          if (reloadEvents) this.loadEvents()
        }
      },
      maybeRecalculatePrice() {
        if (this.initialPrice > 0 && this.per60MinuteDuration > 0 && this.duration > 0) {
          this.price = _.round(this.initialPrice * this.duration/this.per60MinuteDuration, 2)
        }
      },
      loadEvents() {
        if (!this.start || !this.end) return

        this.busy = true
        this.error = null

        const promises = []

        promises.push(this.fetchEvents(this.start, this.end))
        if (this.organization.has_staff_schedule && this.fetchStaffAvailabilityEvents && this.location_id) {
          promises.push(this.fetchStaffAvailabilityEvents([this.location_id]))
        }
        Promise.all(promises)
          .then(results => {
            if (this.fetchStaffAvailabilityEvents && this.location_id) {
              this.staffAvailabilityEvents = results[1]
              this.events = results[0]
            } else {
              this.events = results[0]
            }
            this.hydrateLocationOptions()
          })
        .catch(error => {
          this.error = error
        })
        .finally(() => {
          this.busy = false
        })
      },
      hydrateLocationOptions() {
        this.locationOptions = _.buildNestedLocationOptions(this.locations || [], null, this.conflictingLocationIds, this.unavailableLocationIds)
      },
    },
    mounted() {
      window['bookLocation_' + this.index] = this
      this.hydrateLocationOptions()
      this.$refs.location.$el.addEventListener('keydown', this.onLocationKeydown, {capture: true})
      this.$nextTick(this.loadEvents)
    },
  }
</script>